import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { api, API_ERROR_ACTION_RESERVED_ADMIN } from "../../../lib/axios";
import { downloadGeographieImportTemplate } from "../../../lib/geographieImportTemplate";
import GeoEntityModal, { type GeoEntityMode } from "../../../components/geo/GeoEntityModal";
import { useAuth } from "../../../context/AuthContext";
import { hasPermission } from "../../../config/adminRoutes";

const RESERVE_ADMIN_TOOLTIP = "Action réservée administrateur";

type Province = { id: number; nom: string };
type Ville = { id: number; nom: string; provinceId: number };
type Commune = { id: number; nom: string; villeId: number };
type Quartier = { id: number; nom: string; communeId: number };

export default function GeographieReferentielPage(): React.JSX.Element {
  const { user } = useAuth();
  const canManageGeo = hasPermission(user, "geographie.manage");

  const [provinces, setProvinces] = useState<Province[]>([]);
  const [villes, setVilles] = useState<Ville[]>([]);
  const [communes, setCommunes] = useState<Commune[]>([]);
  const [quartiers, setQuartiers] = useState<Quartier[]>([]);

  const [selectedProvince, setSelectedProvince] = useState<number | null>(null);
  const [selectedVille, setSelectedVille] = useState<number | null>(null);
  const [selectedCommune, setSelectedCommune] = useState<number | null>(null);

  const [qProvince, setQProvince] = useState("");
  const [qVille, setQVille] = useState("");
  const [qCommune, setQCommune] = useState("");
  const [qQuartier, setQQuartier] = useState("");

  const [modalMode, setModalMode] = useState<GeoEntityMode | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importRows, setImportRows] = useState<Record<string, unknown>[]>([]);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [provincesLoadError, setProvincesLoadError] = useState<string | null>(null);

  async function loadProvinces(): Promise<void> {
    setProvincesLoadError(null);
    try {
      const { data } = await api.get<Province[]>("/geographie/provinces");
      setProvinces(Array.isArray(data) ? data : []);
    } catch (e: unknown) {
      const ax = e as {
        response?: {
          status?: number;
          data?: { message?: string; error?: string; details?: string };
        };
      };
      if (ax.response?.status === 403 && ax.response?.data?.error === API_ERROR_ACTION_RESERVED_ADMIN) {
        return;
      }
      const d = ax.response?.data;
      const msg =
        (typeof d?.message === "string" && d.message.trim() && d.message) ||
        (typeof d?.error === "string" && d.error) ||
        "Référentiel indisponible.";
      setProvincesLoadError(msg);
      setProvinces([]);
    }
  }

  async function loadVilles(provinceId: number | null): Promise<void> {
    if (!provinceId) {
      setVilles([]);
      return;
    }
    const { data } = await api.get<Ville[]>("/geographie/villes", { params: { provinceId } });
    setVilles(Array.isArray(data) ? data : []);
  }

  async function loadCommunes(villeId: number | null): Promise<void> {
    if (!villeId) {
      setCommunes([]);
      return;
    }
    const { data } = await api.get<Commune[]>("/geographie/communes", { params: { villeId } });
    setCommunes(Array.isArray(data) ? data : []);
  }

  async function loadQuartiers(communeId: number | null): Promise<void> {
    if (!communeId) {
      setQuartiers([]);
      return;
    }
    const { data } = await api.get<Quartier[]>("/geographie/quartiers", { params: { communeId } });
    setQuartiers(Array.isArray(data) ? data : []);
  }

  useEffect(() => {
    void loadProvinces();
  }, []);

  useEffect(() => {
    void loadVilles(selectedProvince).catch(() => toast.error("Chargement villes impossible."));
    setSelectedVille(null);
    setSelectedCommune(null);
    setCommunes([]);
    setQuartiers([]);
  }, [selectedProvince]);

  useEffect(() => {
    void loadCommunes(selectedVille).catch(() => toast.error("Chargement communes impossible."));
    setSelectedCommune(null);
    setQuartiers([]);
  }, [selectedVille]);

  useEffect(() => {
    void loadQuartiers(selectedCommune).catch(() => toast.error("Chargement quartiers impossible."));
  }, [selectedCommune]);

  const fp = useMemo(
    () => provinces.filter((p) => p.nom.toLowerCase().includes(qProvince.toLowerCase())),
    [provinces, qProvince]
  );
  const fv = useMemo(
    () => villes.filter((v) => v.nom.toLowerCase().includes(qVille.toLowerCase())),
    [villes, qVille]
  );
  const fc = useMemo(
    () => communes.filter((c) => c.nom.toLowerCase().includes(qCommune.toLowerCase())),
    [communes, qCommune]
  );
  const fq = useMemo(
    () => quartiers.filter((q) => q.nom.toLowerCase().includes(qQuartier.toLowerCase())),
    [quartiers, qQuartier]
  );

  function openModal(mode: GeoEntityMode): void {
    setModalMode(mode);
    setModalOpen(true);
  }

  async function createEntity(payload: {
    nom: string;
    actif: boolean;
    commentaire?: string;
  }): Promise<void> {
    if (!modalMode) return;
    setModalLoading(true);
    try {
      const meta = {
        actif: payload.actif,
        ...(payload.commentaire?.trim()
          ? { commentaire: payload.commentaire.trim() }
          : {}),
      };
      if (modalMode === "province") {
        const { data } = await api.post("/geographie/provinces", { nom: payload.nom, ...meta });
        await loadProvinces();
        if (data?.id) setSelectedProvince(Number(data.id));
      } else if (modalMode === "ville") {
        if (!selectedProvince) return;
        const { data } = await api.post("/geographie/villes", {
          nom: payload.nom,
          provinceId: selectedProvince,
          ...meta,
        });
        await loadVilles(selectedProvince);
        if (data?.id) setSelectedVille(Number(data.id));
      } else if (modalMode === "commune") {
        if (!selectedVille) return;
        const { data } = await api.post("/geographie/communes", {
          nom: payload.nom,
          villeId: selectedVille,
          ...meta,
        });
        await loadCommunes(selectedVille);
        if (data?.id) setSelectedCommune(Number(data.id));
      } else if (modalMode === "quartier") {
        if (!selectedCommune) return;
        await api.post("/geographie/quartiers", {
          nom: payload.nom,
          communeId: selectedCommune,
          ...meta,
        });
        await loadQuartiers(selectedCommune);
      }
      toast.success("Creation reussie.");
      setModalOpen(false);
    } catch (e: unknown) {
      const ax = e as { response?: { status?: number; data?: { error?: string } } };
      if (ax.response?.status === 403 && ax.response?.data?.error === API_ERROR_ACTION_RESERVED_ADMIN) {
        return;
      }
      toast.error(ax.response?.data?.error ?? "Creation impossible.");
    } finally {
      setModalLoading(false);
    }
  }

  async function removeEntity(mode: GeoEntityMode, id: number): Promise<void> {
    try {
      await api.delete(`/geographie/${mode}s/${id}`);
      toast.success("Suppression reussie.");
      if (mode === "province") await loadProvinces();
      if (mode === "ville" && selectedProvince) await loadVilles(selectedProvince);
      if (mode === "commune" && selectedVille) await loadCommunes(selectedVille);
      if (mode === "quartier" && selectedCommune) await loadQuartiers(selectedCommune);
    } catch (e: unknown) {
      const ax = e as {
        response?: {
          status?: number;
          data?: { error?: string; message?: string };
        };
      };
      const d = ax.response?.data;
      if (ax.response?.status === 409) {
        toast.error(
          (typeof d?.message === "string" && d.message.trim() ? d.message : null) ??
            d?.error ??
            "Suppression bloquée : entité encore utilisée."
        );
      } else if (ax.response?.status === 403 && d?.error === API_ERROR_ACTION_RESERVED_ADMIN) {
        return;
      } else {
        toast.error(
          (typeof d?.message === "string" && d.message.trim() ? d.message : null) ??
            "Suppression impossible."
        );
      }
    }
  }

  const parentLabel =
    modalMode === "ville"
      ? provinces.find((p) => p.id === selectedProvince)?.nom
      : modalMode === "commune"
        ? villes.find((v) => v.id === selectedVille)?.nom
        : modalMode === "quartier"
          ? communes.find((c) => c.id === selectedCommune)?.nom
          : undefined;

  function ColumnCard(props: {
    title: string;
    query: string;
    onQuery: (v: string) => void;
    addLabel: string;
    onAdd: () => void;
    addDisabled?: boolean;
    /** Infobulle quand `addDisabled` (parent non sélectionné) — pas utilisé si `canManage` est false. */
    addDisabledHint?: string;
    empty: string;
    items: { id: number; nom: string }[];
    selectedId: number | null;
    onSelect: (id: number) => void;
    onDelete: (id: number) => void;
    canManage?: boolean;
  }): React.JSX.Element {
    const canManage = props.canManage !== false;
    const addBlockedByParent = Boolean(props.addDisabled);
    const addButtonTitle = !canManage
      ? RESERVE_ADMIN_TOOLTIP
      : addBlockedByParent && props.addDisabledHint
        ? props.addDisabledHint
        : undefined;
    return (
      <section className="rounded-2xl border border-slate-800 bg-slate-900/50 p-3">
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-white">{props.title}</h2>
          <button
            type="button"
            disabled={props.addDisabled || !canManage}
            title={addButtonTitle}
            onClick={props.onAdd}
            className="rounded-lg border border-emerald-700/40 bg-emerald-950/40 px-2 py-1 text-xs text-emerald-200 disabled:opacity-40"
          >
            {props.addLabel}
          </button>
        </div>
        <input
          value={props.query}
          onChange={(e) => props.onQuery(e.target.value)}
          placeholder="Recherche"
          className="mb-2 w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-xs"
        />
        <div className="max-h-[60vh] space-y-1 overflow-y-auto">
          {props.items.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-700 px-3 py-6 text-center text-xs text-slate-500">
              {props.empty}
            </div>
          ) : (
            props.items.map((it) => (
              <div
                key={it.id}
                className={`flex items-center justify-between rounded-lg border px-2 py-1.5 ${
                  props.selectedId === it.id
                    ? "border-sky-500/40 bg-sky-500/10"
                    : "border-slate-800 bg-slate-900/60"
                }`}
              >
                <button
                  type="button"
                  onClick={() => props.onSelect(it.id)}
                  className="text-left text-xs text-slate-200"
                >
                  {it.nom}
                </button>
                <button
                  type="button"
                  disabled={!canManage}
                  title={!canManage ? RESERVE_ADMIN_TOOLTIP : undefined}
                  onClick={() => void props.onDelete(it.id)}
                  className="text-[10px] text-rose-300 hover:text-rose-200 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Suppr.
                </button>
              </div>
            ))
          )}
        </div>
      </section>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 px-4 py-6 text-slate-100 md:px-8">
      <header className="mb-5 border-b border-slate-800 pb-4">
        <h1 className="text-2xl font-bold text-white">Referentiel geographique</h1>
        <p className="mt-1 text-sm text-slate-400">
          {canManageGeo
            ? "Hiérarchie obligatoire : choisissez une province, puis une ville, puis une commune, avant d’ajouter le niveau suivant. Les boutons + à droite restent grisés tant que le parent n’est pas sélectionné (clic sur une ligne)."
            : "Consultation du referentiel (provinces, villes, communes, quartiers). Les modifications sont reservees aux administrateurs."}
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => downloadGeographieImportTemplate()}
            className="rounded-lg border border-sky-700/40 bg-sky-950/30 px-3 py-1.5 text-xs font-medium text-sky-200 hover:bg-sky-950/50"
          >
            Télécharger le modèle Excel
          </button>
          <button
            type="button"
            disabled={!canManageGeo}
            title={!canManageGeo ? RESERVE_ADMIN_TOOLTIP : undefined}
            onClick={() => setImportOpen(true)}
            className="rounded-lg border border-emerald-700/40 bg-emerald-950/30 px-3 py-1.5 text-xs font-medium text-emerald-200 hover:bg-emerald-950/50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Importer referentiel geographique
          </button>
        </div>
      </header>

      {provincesLoadError ? (
        <div className="mb-4 rounded-xl border border-rose-800/50 bg-rose-950/25 p-4 text-sm text-rose-100">
          <p className="font-medium text-rose-50">Référentiel indisponible</p>
          <p className="mt-1 text-rose-100/90">{provincesLoadError}</p>
          <button
            type="button"
            onClick={() => void loadProvinces()}
            className="mt-3 rounded-lg border border-rose-600/50 bg-rose-900/40 px-3 py-1.5 text-xs font-semibold text-rose-50 hover:bg-rose-900/60"
          >
            Réessayer
          </button>
        </div>
      ) : null}

      <div className="grid gap-3 xl:grid-cols-4">
        <ColumnCard
          title="Provinces"
          query={qProvince}
          onQuery={setQProvince}
          addLabel="+ Province"
          onAdd={() => openModal("province")}
          empty="Aucune province."
          items={fp}
          selectedId={selectedProvince}
          onSelect={setSelectedProvince}
          onDelete={(id) => removeEntity("province", id)}
          canManage={canManageGeo}
        />
        <ColumnCard
          title="Villes"
          query={qVille}
          onQuery={setQVille}
          addLabel="+ Ville"
          onAdd={() => openModal("ville")}
          addDisabled={!selectedProvince}
          addDisabledHint="Sélectionnez d’abord une province (clic sur son nom dans la colonne de gauche)."
          empty="Selectionnez une province."
          items={fv}
          selectedId={selectedVille}
          onSelect={setSelectedVille}
          onDelete={(id) => removeEntity("ville", id)}
          canManage={canManageGeo}
        />
        <ColumnCard
          title="Communes"
          query={qCommune}
          onQuery={setQCommune}
          addLabel="+ Commune"
          onAdd={() => openModal("commune")}
          addDisabled={!selectedVille}
          addDisabledHint="Sélectionnez d’abord une ville (clic sur son nom dans la colonne Villes)."
          empty="Selectionnez une ville."
          items={fc}
          selectedId={selectedCommune}
          onSelect={setSelectedCommune}
          onDelete={(id) => removeEntity("commune", id)}
          canManage={canManageGeo}
        />
        <ColumnCard
          title="Quartiers"
          query={qQuartier}
          onQuery={setQQuartier}
          addLabel="+ Quartier"
          onAdd={() => openModal("quartier")}
          addDisabled={!selectedCommune}
          addDisabledHint="Sélectionnez d’abord une commune (clic sur son nom dans la colonne Communes)."
          empty="Selectionnez une commune."
          items={fq}
          selectedId={null}
          onSelect={() => void 0}
          onDelete={(id) => removeEntity("quartier", id)}
          canManage={canManageGeo}
        />
      </div>

      <GeoEntityModal
        open={modalOpen}
        mode={modalMode ?? "province"}
        parentLabel={parentLabel}
        loading={modalLoading}
        onClose={() => setModalOpen(false)}
        onSubmit={createEntity}
      />

      {importOpen ? (
        <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/70 p-4">
          <div className="w-full max-w-4xl rounded-2xl border border-slate-800 bg-slate-900 p-5">
            <h3 className="text-lg font-semibold text-white">Import referentiel geographique</h3>
            <p className="mt-1 text-xs text-slate-500">
              Preview obligatoire avant import. Colonnes attendues: Province, Ville, Commune, Quartier.
            </p>
            <input
              type="file"
              accept=".xlsx,.xls"
              className="mt-3 block text-sm"
              onChange={(e) => {
                const f = e.target.files?.[0] ?? null;
                setImportFile(f);
                if (!f) {
                  setImportRows([]);
                  return;
                }
                const reader = new FileReader();
                reader.onload = () => {
                  const data = new Uint8Array(reader.result as ArrayBuffer);
                  const wb = XLSX.read(data, { type: "array" });
                  const sheet = wb.Sheets[wb.SheetNames[0]];
                  const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet);
                  setImportRows(rows);
                };
                reader.readAsArrayBuffer(f);
              }}
            />
            <div className="mt-4 max-h-[40vh] overflow-auto rounded-xl border border-slate-800">
              {importRows.length === 0 ? (
                <p className="p-4 text-xs text-slate-500">Aucune ligne previsualisee.</p>
              ) : (
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-slate-800 text-slate-500">
                      <th className="px-2 py-1 text-left">Province</th>
                      <th className="px-2 py-1 text-left">Ville</th>
                      <th className="px-2 py-1 text-left">Commune</th>
                      <th className="px-2 py-1 text-left">Quartier</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importRows.slice(0, 20).map((r, i) => (
                      <tr key={i} className="border-b border-slate-900/80">
                        <td className="px-2 py-1">{String(r.Province ?? "")}</td>
                        <td className="px-2 py-1">{String(r.Ville ?? "")}</td>
                        <td className="px-2 py-1">{String(r.Commune ?? "")}</td>
                        <td className="px-2 py-1">{String(r.Quartier ?? "")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setImportOpen(false);
                  setImportRows([]);
                  setImportFile(null);
                }}
                className="rounded-lg px-3 py-1.5 text-sm text-slate-400"
              >
                Annuler
              </button>
              <button
                type="button"
                disabled={importing || !importFile || importRows.length === 0 || !canManageGeo}
                title={!canManageGeo ? RESERVE_ADMIN_TOOLTIP : undefined}
                onClick={async () => {
                  if (!importFile) return;
                  setImporting(true);
                  try {
                    const fd = new FormData();
                    fd.append("file", importFile);
                    const { data } = await api.post<{
                      crees?: number;
                      ignoresDoublons?: number;
                      erreurs?: { ligne: number; message: string }[];
                      totalLignesFichier?: number;
                    }>("/geographie/import-excel", fd, {
                      headers: { "Content-Type": "multipart/form-data" },
                    });
                    const errN = data?.erreurs?.length ?? 0;
                    toast.success(
                      `Import terminé : ${data?.crees ?? 0} créé(s), ${data?.ignoresDoublons ?? 0} doublon(s) ignoré(s), ${errN} erreur(s).`
                    );
                    setImportOpen(false);
                    await loadProvinces();
                    await loadVilles(selectedProvince);
                    await loadCommunes(selectedVille);
                    await loadQuartiers(selectedCommune);
                  } catch (e: unknown) {
                    const ax = e as { response?: { status?: number; data?: { error?: string } } };
                    if (ax.response?.status === 403 && ax.response?.data?.error === API_ERROR_ACTION_RESERVED_ADMIN) {
                      return;
                    }
                    toast.error("Import impossible.");
                  } finally {
                    setImporting(false);
                  }
                }}
                className="rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-emerald-500 disabled:opacity-40"
              >
                {importing ? "Import..." : "Importer"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
